import { Controller } from "@hotwired/stimulus"

// Proactively signs the user out after a configurable idle period.
// Devise's :timeoutable blocks the NEXT request after expiry, but an
// open tab showing PHI doesn't refresh. This controller listens for
// keyboard, mouse, and touch events and posts to the sign-out endpoint
// when the idle threshold is reached.
//
// Usage:
//   <body data-controller="idle-watcher"
//         data-idle-watcher-timeout-value="900000"
//         data-idle-watcher-signout-url-value="/clients/sign_out"
//         data-idle-watcher-redirect-url-value="/acme/login">
//
// Default timeout: 15 minutes (900_000 ms). Match the model's
// `timeout_in` to avoid race conditions with Devise.
//
// redirectUrl: where to send the browser after the sign-out POST.
// Defaults to "/" (public root) for safety, but the portal layout
// overrides this to the practice-scoped sign-in page so re-auth
// keeps the practice context (CP-9).
//
// CP-14: multi-tab coordination via BroadcastChannel. Without it,
// each open tab runs an independent idle timer — an active user in
// Tab A would still get signed out by an idle Tab B, ending the
// session prematurely. Activity in any tab now broadcasts a reset
// to its siblings (with localStorage fallback for older browsers)
// so all tabs stay in sync.
export default class extends Controller {
  static values = {
    timeout: { type: Number, default: 900000 },
    signoutUrl: { type: String, default: "/clients/sign_out" },
    redirectUrl: { type: String, default: "/" },
    channelName: { type: String, default: "idle-watcher" }
  }

  connect() {
    this.broadcastReset = this.broadcastReset.bind(this)
    this.resetTimerSilently = this.resetTimerSilently.bind(this)
    this.onChannelMessage = this.onChannelMessage.bind(this)
    this.onStorage = this.onStorage.bind(this)
    this.fireSignOut = this.fireSignOut.bind(this)

    this.events = ["mousemove", "keydown", "click", "touchstart", "scroll"]
    this.events.forEach((evt) => {
      window.addEventListener(evt, this.broadcastReset, { passive: true })
    })

    if (typeof BroadcastChannel !== "undefined") {
      this.channel = new BroadcastChannel(this.channelNameValue)
      this.channel.addEventListener("message", this.onChannelMessage)
    } else {
      // localStorage 'storage' events fire in OTHER tabs only, which is
      // exactly the cross-tab semantics we want from the fallback.
      this.storageKey = `${this.channelNameValue}:reset`
      window.addEventListener("storage", this.onStorage)
    }

    this.resetTimerSilently()
  }

  disconnect() {
    this.events.forEach((evt) => {
      window.removeEventListener(evt, this.broadcastReset)
    })
    if (this.channel) {
      this.channel.removeEventListener("message", this.onChannelMessage)
      this.channel.close()
      this.channel = null
    } else {
      window.removeEventListener("storage", this.onStorage)
    }
    if (this.timerId) clearTimeout(this.timerId)
  }

  // Local activity: reset our own timer AND tell sibling tabs to
  // reset theirs. We use a distinct method (vs. resetTimerSilently)
  // so inbound messages can short-circuit re-broadcasting and avoid
  // a feedback loop.
  broadcastReset() {
    this.resetTimerSilently()
    if (this.channel) {
      try {
        this.channel.postMessage({ type: "reset", at: Date.now() })
      } catch (_e) {
        // Channel may be closed during navigation; safe to ignore.
      }
    } else if (this.storageKey) {
      try {
        // Value must change to fire the 'storage' event in other tabs.
        window.localStorage.setItem(this.storageKey, String(Date.now()))
      } catch (_e) {
        // Storage quota / private mode — local timer is still reset.
      }
    }
  }

  resetTimerSilently() {
    if (this.timerId) clearTimeout(this.timerId)
    this.timerId = setTimeout(this.fireSignOut, this.timeoutValue)
  }

  onChannelMessage(event) {
    if (event?.data?.type === "reset") {
      this.resetTimerSilently()
    }
  }

  onStorage(event) {
    if (event.key === this.storageKey) {
      this.resetTimerSilently()
    }
  }

  async fireSignOut() {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      await fetch(this.signoutUrlValue, {
        method: "DELETE",
        headers: { "X-CSRF-Token": token, Accept: "text/html" },
        // PT-HIGH-7: explicit same-origin so the portal session cookie
        // is sent. The default IS same-origin in modern browsers, but
        // relying on the default for a security-critical sign-out call
        // is fragile against future fetch-default changes or a misconfigured
        // CORS proxy.
        credentials: "same-origin"
      })
    } catch (e) {
      // Network failure — best effort; full redirect below will trigger
      // server-side timeoutable check on next request.
    }
    window.location.href = this.redirectUrlValue
  }
}
